import { BiCheckCircle } from 'react-icons/bi';
import { FiEdit } from 'react-icons/fi';
import { RiErrorWarningLine } from 'react-icons/ri';
import LateralPopover from '../../../../components/lib/lateralPopover';
import ModalInfo from '../../../../components/lib/modalInfo';
import { formatDate } from '../../../../utils/DateUtils';
import { formatCurrency } from '../../../../utils/CurrencyUtils';
import DetailsModal from '../../../../components/registryDetailsModal';

const OnlineInfoRow = ({ label, count, total, details, type, index }) => (
    <div className="flex items-center justify-between p-3 mb-2 border border-purple-100 bg-purple-50 rounded-lg">
        <div className="flex flex-col flex-1">
            <span className="text-[10px] font-bold uppercase mb-0.5 text-purple-400">{type} Online</span>
            <span className="font-semibold text-purple-900 text-sm">{label}</span>
            {details && details.length > 0 && (
                <div className="mt-1">
                    {type === "Venda" ? <DetailsModal details={details} /> : <LateralPopover details={details} buttonIndex={`online-${type}-${index}`} />}
                </div>
            )}
        </div>
        <div className="flex flex-col items-end">
            <span className="font-bold text-purple-800">{formatCurrency(total)}</span>
            <span className="text-xs text-purple-500 bg-white px-2 py-0.5 rounded-full">{count}x</span>
        </div>
    </div>
);

const CreditoFoodzInfoRow = ({ label, count, total, details, index }) => (
    <div className="flex items-center justify-between p-3 mb-2 border border-amber-100 bg-amber-50 rounded-lg">
        <div className="flex flex-col flex-1">
            <span className="text-[10px] font-bold uppercase mb-0.5 text-amber-500">Venda Crédito Foodz</span>
            <span className="font-semibold text-amber-900 text-sm">{label}</span>
            {details && details.length > 0 && (
                <div className="mt-1">
                    <DetailsModal details={details} />
                </div>
            )}
        </div>
        <div className="flex flex-col items-end">
            <span className="font-bold text-amber-800">{formatCurrency(total)}</span>
            <span className="text-xs text-amber-600 bg-white px-2 py-0.5 rounded-full">{count}x</span>
        </div>
    </div>
);

const ConferenceRow = ({ label, count, total, type, details, onConfirm, isChecked, disabled, loading, index }) => (
    <div className={`flex items-center justify-between p-3 mb-2 border rounded-lg transition-all ${isChecked ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200 hover:border-blue-300 shadow-sm'}`}>
        <div className="flex flex-col flex-1">
            <span className="text-[10px] font-bold uppercase mb-0.5 text-gray-400">{type}</span>
            <span className="font-semibold text-gray-700 text-sm">{label}</span>
            {details && details.length > 0 && (
                <div className="mt-1">
                    {type === "Venda" ? <DetailsModal details={details} /> : <LateralPopover details={details} buttonIndex={`${type}-${index}`} />}
                </div>
            )}
        </div>
        <div className="flex flex-col items-end mr-4">
            <span className="font-bold text-gray-800">{formatCurrency(total)}</span>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{count}x</span>
        </div>
        <button
            onClick={onConfirm}
            disabled={loading}
            className={`p-2 rounded-full transition-all duration-200 ${isChecked
                ? 'text-green-600 bg-green-100 hover:bg-green-200 scale-110'
                : 'text-gray-300 hover:text-green-500 hover:bg-slate-100'
                }`}
            title={isChecked ? "Desmarcar conferência" : "Confirmar conferência"}
        >
            <BiCheckCircle className="text-2xl" />
        </button>
    </div>
);

const ClosedCashView = ({
    dailyCashFlow, inputValue, outputValue,
    pedidoEntries = [], recargaEntries = [], correcaoEntries = [],
    pedidoOnlineEntries = [], recargaOnlineEntries = [],
    pedidoCreditoFoodzEntries = [],
    disabledButtons, isFinishButtonEnabled, loading,
    handleButtonClick, handleBackClick, finishCashBalanceRegistry, handleEditClick
}) => {
    return (
        <div className='flex flex-col h-full space-y-6'>
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-3 shrink-0">
                <div className='flex justify-between items-center text-sm'>
                    <span className='text-gray-500'>Abertura</span>
                    <span className='font-mono font-semibold'>{formatDate(dailyCashFlow.opening_time)}</span>
                </div>
                <div className='flex justify-between items-center text-sm border-b border-slate-200 pb-3'>
                    <span className='text-gray-500'>Valor Inicial</span>
                    <span className='font-bold text-gray-800'>{formatCurrency(dailyCashFlow.start_value)}</span>
                </div>

                <div className='flex justify-between items-center text-sm'>
                    <span className='text-green-600 font-bold'>Entradas (+):</span>
                    <div className='flex items-center gap-2'>
                        <span className='font-bold text-gray-800'>{formatCurrency(inputValue)}</span>
                        <button onClick={() => handleEditClick("inputValue")} className="text-gray-400 hover:text-custom_blue"><FiEdit /></button>
                    </div>
                </div>
                <div className='flex justify-between items-center text-sm'>
                    <span className='text-red-600 font-bold'>Saídas (-):</span>
                    <div className='flex items-center gap-2'>
                        <span className='font-bold text-gray-800'>{formatCurrency(outputValue)}</span>
                        <button onClick={() => handleEditClick("outputValue")} className="text-gray-400 hover:text-custom_blue"><FiEdit /></button>
                    </div>
                </div>

                <div className='flex justify-between items-center text-lg bg-white p-3 rounded-lg border border-slate-200 mt-2'>
                    <span className='font-bold text-gray-800'>Valor Final Previsto:</span>
                    <span className='font-black text-custom_blue'>{formatCurrency(dailyCashFlow.final_value)}</span>
                </div>
            </div>

            <div className='flex-1 overflow-y-auto pr-1 custom-scrollbar'>
                <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 tracking-wide">Itens para Conferência</h3>

                {pedidoEntries.map((item, idx) => (
                    <ConferenceRow
                        key={`ped-${idx}`} type="Venda" label={item.tipoNormalized} count={item.count} total={item.total} details={item.details}
                        onConfirm={() => handleButtonClick(item.tipoOriginal, item.count, item.total)}
                        isChecked={disabledButtons[item.tipoOriginal]}
                        loading={loading}
                    />
                ))}

                {recargaEntries.map((item, idx) => (
                    <ConferenceRow
                        key={`rec-${idx}`} type="Recarga" index={idx} label={item.tipoNormalized} count={item.count} total={item.total} details={item.details}
                        onConfirm={() => handleButtonClick(item.tipoOriginal, item.count, item.total)}
                        isChecked={disabledButtons[item.tipoOriginal]}
                        loading={loading}
                    />
                ))}

                {correcaoEntries.map((item, idx) => (
                    <ConferenceRow
                        key={`cor-${idx}`} type="Correção" index={idx} label={item.tipoNormalized} count={item.count} total={item.total} details={item.details}
                        onConfirm={() => handleButtonClick(item.tipoOriginal, item.count, item.total)}
                        isChecked={disabledButtons[item.tipoOriginal]}
                        loading={loading}
                    />
                ))}

                {(pedidoOnlineEntries.length > 0 || recargaOnlineEntries.length > 0) && (
                    <div className="mt-4">
                        <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-xs font-bold text-purple-500 uppercase tracking-wide">Pagamentos Online (informativo)</h4>
                            <ModalInfo icon={<RiErrorWarningLine />} content="Esse valor já está na carteira digital, não no caixa físico — não soma ao Valor Final." />
                        </div>
                        {pedidoOnlineEntries.map((item, idx) => (
                            <OnlineInfoRow key={`ped-online-${idx}`} type="Venda" index={idx} label={item.tipoNormalized} count={item.count} total={item.total} details={item.details} />
                        ))}
                        {recargaOnlineEntries.map((item, idx) => (
                            <OnlineInfoRow key={`rec-online-${idx}`} type="Recarga" index={idx} label={item.tipoNormalized} count={item.count} total={item.total} details={item.details} />
                        ))}
                    </div>
                )}

                {pedidoCreditoFoodzEntries.length > 0 && (
                    <div className="mt-4">
                        <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-xs font-bold text-amber-500 uppercase tracking-wide">Vendas com Crédito Foodz (informativo)</h4>
                            <ModalInfo icon={<RiErrorWarningLine />} content="Esse valor já entrou no caixa quando o crédito foi recarregado — não soma de novo ao Valor Final." />
                        </div>
                        {pedidoCreditoFoodzEntries.map((item, idx) => (
                            <CreditoFoodzInfoRow key={`ped-foodz-${idx}`} index={idx} label={item.tipoNormalized} count={item.count} total={item.total} details={item.details} />
                        ))}
                    </div>
                )}
            </div>

            <div className="flex gap-3 pt-4 border-t border-slate-100 shrink-0">
                <button
                    className="flex-1 bg-white border border-gray-300 text-gray-700 font-bold py-3 rounded-xl hover:bg-gray-50 transition-colors"
                    onClick={handleBackClick}
                    disabled={loading}
                >
                    Voltar
                </button>
                <button
                    className={`flex-1 font-bold py-3 rounded-xl text-white shadow-md transition-all ${!isFinishButtonEnabled ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                    onClick={finishCashBalanceRegistry}
                    disabled={loading || !isFinishButtonEnabled}
                >
                    Finalizar
                </button>
            </div>
        </div>
    );
};

export default ClosedCashView;